import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import type { ActionDetail, EmailDto, PollCreate, PollDto, PollParticipantDto, PollRole } from '@tww/shared';
import { fmtDate, fmtPollDate, formatAddress, POLL_ANSWER_LABELS, POLL_ROLE_LABELS, pollDateScore, todayIso } from '@tww/shared';
import { api, ApiRequestError } from '../../api/client';
import { useAuth } from '../../auth/AuthProvider';
import { Note, Panel } from '../../ui/basics';
import { ConfirmDialog, Modal } from '../../ui/Modal';
import { Checkbox, TextArea } from '../../ui/fields';
import { ContactPicker } from '../../ui/ContactPicker';
import { Icon } from '../../ui/Icon';
import { useToast } from '../../ui/Toast';
import { daysBetween, isSunday, MonthCalendar } from '../../ui/MonthCalendar';
import '../../styles/polls.css';

const linkOf = (token: string) => `${window.location.origin}/abstimmung/${token}`;
type Draft = { dates: string[]; participants: { role: PollRole; contact_id: number | null; name: string; on: boolean }[]; message: string };

/**
 * Terminabstimmung im Tab „Beteiligte“: Tage vorschlagen, persönliche Links verschicken, Antworten sehen, Tag wählen.
 * Steht über der Liste, solange sie aussteht (noch keine oder laufende Abstimmung), danach darunter.
 */
export function PollPanel({ a, place }: { a: ActionDetail; place: 'top' | 'bottom' }) {
  const qc = useQueryClient();
  const toast = useToast();
  const { can, me } = useAuth();
  const write = can('actions', 'write');
  const [sp, setSp] = useSearchParams();
  const q = useQuery({ queryKey: ['poll', a.id], queryFn: () => api.get<{ poll: PollDto | null }>(`/actions/${a.id}/poll`).then((r) => r.poll) });
  const [edit, setEdit] = useState<'new' | 'edit' | null>(null);
  const [choose, setChoose] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['poll', a.id] });
    qc.invalidateQueries({ queryKey: ['action', a.id] });
    qc.invalidateQueries({ queryKey: ['actions'] });
  };
  const chooseM = useMutation({
    mutationFn: (date: string) => api.post<{ poll: PollDto; confirmed: string[] }>(`/actions/${a.id}/poll/choose`, { date, confirm: true }),
    onSuccess: (r) => {
      refresh();
      setChoose(null);
      toast(`Aktionstag ${fmtDate(r.poll.chosen_date)} gesetzt${r.confirmed.length ? ` · Termin bestätigt: ${r.confirmed.join(', ')}` : ''}`);
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const closeM = useMutation({ mutationFn: () => api.post(`/actions/${a.id}/poll/close`), onSuccess: () => (refresh(), toast('Abstimmung beendet')), onError: (e: Error) => toast(e.message, 'error') });
  const deleteM = useMutation({ mutationFn: () => api.delete(`/actions/${a.id}/poll`), onSuccess: () => (refresh(), setConfirmDelete(false), toast('Abstimmung gelöscht')), onError: (e: Error) => toast(e.message, 'error') });
  const mail = useMutation({
    mutationFn: (x: PollParticipantDto) =>
      api.post<EmailDto>('/emails', {
        entity_type: 'action',
        entity_id: a.id,
        to: x.email ? [formatAddress(x.name, x.email)] : [],
        subject: `Terminabstimmung Pflanzaktion${a.partner ? ` mit ${a.partner.display_name}` : ''}`,
        body: `Guten Tag,\n\nfür unsere Pflanzaktion${a.partner ? ` mit ${a.partner.display_name}` : ''}${a.city ? ` in ${a.city}` : ''} suchen wir einen gemeinsamen Termin. Bitte tragen Sie unter diesem Link ein, welche Tage Ihnen passen:\n\n${linkOf(x.token)}\n\nDer Link ist persönlich für Sie. Vielen Dank!\n\nMit freundlichen Grüßen\n${me?.user.display_name ?? ''}`,
      }),
    onSuccess: (m) => {
      const n = new URLSearchParams(sp);
      n.set('tab', 'emails');
      n.set('mail_id', String(m.id));
      setSp(n, { replace: true });
    },
    onError: (e: Error) => toast(e.message, 'error'),
  });
  const copy = async (x: PollParticipantDto) => {
    try {
      await navigator.clipboard.writeText(linkOf(x.token));
      toast(`Link für ${x.name} kopiert`);
    } catch {
      window.prompt('Link kopieren', linkOf(x.token));
    }
  };

  const p = q.data;
  const closedAction = a.status === 'abgeschlossen' || a.status === 'ausgefallen';
  const best = useMemo(() => {
    if (!p?.dates.length) return null;
    const top = [...p.dates].sort((x, y) => pollDateScore(y) - pollDateScore(x))[0]!;
    return top.yes + top.maybe > 0 ? top.date : null;
  }, [p]);
  const byDate = useMemo(() => new Map((p?.dates ?? []).map((x) => [x.date, x])), [p]);
  if (q.isLoading) return null;
  if (!p && (closedAction || !write)) return null;
  const pending = !p || p.status === 'open';
  if ((place === 'top') !== pending) return null;

  const startButton = write && !closedAction && (
    <button className="btn sm" type="button" onClick={() => setEdit('new')}>
      <Icon name="plus" /> {p ? 'Neue Abstimmung' : 'Terminabstimmung starten'}
    </button>
  );
  return (
    <>
      {!p ? (
        <Panel title="Terminabstimmung" action={startButton}>
          <div className="pb mut">Tage vorschlagen und Förster und Spendenpartner per Link abstimmen lassen.</div>
        </Panel>
      ) : p.status === 'closed' ? (
        <Panel title="Terminabstimmung" action={startButton}>
          <div className="pb">
            {p.chosen_date ? `Abgestimmt: ${fmtDate(p.chosen_date)} gewählt` : 'Beendet ohne Tag'}
            {p.closed_at ? <span className="mut"> · am {fmtDate(p.closed_at)}</span> : null}
            {' · '}
            {p.participants.filter((x) => x.responded_at).length} von {p.participants.length} haben geantwortet
          </div>
        </Panel>
      ) : (
        <Panel
          title="Terminabstimmung läuft"
          action={
            write && (
              <span style={{ display: 'inline-flex', gap: 6 }}>
                <button className="btn sm" type="button" onClick={() => setEdit('edit')}>
                  <Icon name="edit" /> Bearbeiten
                </button>
                <button className="btn sm" type="button" onClick={() => closeM.mutate()} disabled={closeM.isPending} title="Abstimmung beenden, ohne einen Tag zu wählen">
                  Beenden
                </button>
              </span>
            )
          }
        >
          <div className="pb">
            <MonthCalendar
              start={p.dates[0]!.date.slice(0, 7)}
              min={p.dates[0]!.date.slice(0, 7)}
              max={p.dates[p.dates.length - 1]!.date.slice(0, 7)}
              months={p.dates[0]!.date.slice(0, 7) === p.dates[p.dates.length - 1]!.date.slice(0, 7) ? 1 : 2}
              enabled={(x) => write && byDate.has(x)}
              dayClass={(x) => {
                const r = byDate.get(x);
                if (!r) return '';
                const ok = r.yes + r.maybe;
                return `res ${r.fits_all ? 'all' : ok > 0 ? 'some' : r.no > 0 ? 'none' : ''} ${x === best ? 'best' : ''}`;
              }}
              dayExtra={(x) => {
                const r = byDate.get(x);
                return r ? <span className="x">{r.yes + r.maybe}/{p.participants.length}</span> : null;
              }}
              dayTitle={(x) => {
                const r = byDate.get(x);
                if (!r) return undefined;
                const who = p.participants.map((y) => `${y.name}: ${y.answers[x] ? POLL_ANSWER_LABELS[y.answers[x]!] : y.responded_at ? 'nicht möglich' : 'keine Antwort'}`);
                const others = r.other_actions.map((o) => `Andere Aktion: ${o.label}`);
                return [...who, ...others, write ? 'Klicken, um diesen Tag zu wählen' : ''].filter(Boolean).join('\n');
              }}
              onDayClick={(x) => setChoose(x)}
            />
            <div className="mcal-legend">
              <span><i className="all" />für alle möglich</span>
              <span><i className="some" />für einige möglich</span>
              <span><i className="none" />für niemanden möglich</span>
              <span>Zahl: Zusagen von {p.participants.length}</span>
            </div>
          </div>
          <div className="tw">
            <table className="poll-matrix">
              <thead>
                <tr>
                  <th>Wer</th>
                  {p.dates.map((d) => (
                    <th key={d.date} className={d.date === best ? 'best' : undefined} title={d.fits_all ? 'für alle möglich' : undefined}>
                      {fmtPollDate(d.date)}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {p.participants.map((x) => (
                  <tr key={x.id}>
                    <td>
                      {x.name} <span className="mut small">({x.role_label})</span>
                      {x.comment && (
                        <span className="sm" title={x.comment}>
                          „{x.comment.length > 50 ? x.comment.slice(0, 50) + '…' : x.comment}“
                        </span>
                      )}
                    </td>
                    {p.dates.map((d) => {
                      const v = x.answers[d.date];
                      return (
                        <td key={d.date} className={d.date === best ? 'best' : undefined}>
                          <span className={`cell ${v ?? 'none'}`}>{v ? POLL_ANSWER_LABELS[v] : x.responded_at ? '–' : '?'}</span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
                <tr>
                  <td className="mut">Andere Aktionen</td>
                  {p.dates.map((d) => (
                    <td key={d.date} className={`others ${d.date === best ? 'best' : ''}`} title={d.other_actions.map((o) => `${o.label}${o.state_label ? ` (${o.state_label})` : ''}`).join('\n')}>
                      {d.other_actions.length ? d.other_actions.map((o) => <div key={o.id}><Link className="link" to={`/aktionen/${o.id}`}>{o.region || o.state_label || o.code}</Link></div>) : <span className="mut">–</span>}
                    </td>
                  ))}
                </tr>
                {write && (
                  <tr>
                    <td />
                    {p.dates.map((d) => (
                      <td key={d.date} className={d.date === best ? 'best' : undefined}>
                        <button className={`btn sm ${d.date === best ? 'primary' : ''}`} type="button" onClick={() => setChoose(d.date)}>
                          wählen
                        </button>
                      </td>
                    ))}
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          <div className="pb poll-links">
            {p.participants.map((x) => (
              <div className="row" key={x.id}>
                <span className="who">
                  {x.name} <span className="mut small">{x.role_label}</span>
                </span>
                <span className={x.responded_at ? 'ok small' : 'mut small'}>{x.responded_at ? `geantwortet ${fmtDate(x.responded_at)}` : 'noch keine Antwort'}</span>
                <button className="btn sm" type="button" onClick={() => copy(x)}>
                  <Icon name="link" /> Link kopieren
                </button>
                {can('emails', 'write') && (
                  <button className="btn sm" type="button" onClick={() => mail.mutate(x)} disabled={mail.isPending} title={x.email ? undefined : 'Keine E-Mail-Adresse hinterlegt; Empfänger im Entwurf ergänzen'}>
                    <Icon name="mail" /> E-Mail
                  </button>
                )}
              </div>
            ))}
          </div>
        </Panel>
      )}
      {edit && (
        <PollModal
          a={a}
          poll={edit === 'edit' ? (p ?? null) : null}
          onClose={() => setEdit(null)}
          onSaved={() => (setEdit(null), refresh())}
          onDelete={edit === 'edit' ? () => (setEdit(null), setConfirmDelete(true)) : undefined}
        />
      )}
      {choose && p && (
        <ConfirmDialog
          title="Tag wählen"
          text={
            <>
              Aktionstag auf <strong>{fmtDate(choose)}</strong> setzen und die Abstimmung beenden. Förster und Spendenpartner, die den Tag als „möglich“ markiert haben, gelten als bestätigt; die Fristen der Checkliste werden neu berechnet.
              {(p.dates.find((d) => d.date === choose)?.other_actions.length ?? 0) > 0 && <Note kind="warn">An diesem Tag gibt es schon {p.dates.find((d) => d.date === choose)!.other_actions.length} andere Aktion(en).</Note>}
            </>
          }
          confirmLabel="Tag übernehmen"
          onConfirm={() => chooseM.mutate(choose)}
          onClose={() => setChoose(null)}
          busy={chooseM.isPending}
        />
      )}
      {confirmDelete && <ConfirmDialog title="Abstimmung löschen" text="Die Abstimmung mit allen Antworten wird gelöscht; die verschickten Links funktionieren dann nicht mehr." confirmLabel="Löschen" danger onConfirm={() => deleteM.mutate()} onClose={() => setConfirmDelete(false)} busy={deleteM.isPending} />}
    </>
  );
}

/** Dialog: Tage im Kalender anklicken oder als Zeitraum ziehen, Teilnehmende, Hinweis */
function PollModal({ a, poll, onClose, onSaved, onDelete }: { a: ActionDetail; poll: PollDto | null; onClose: () => void; onSaved: () => void; onDelete?: () => void }) {
  const toast = useToast();
  const existingDates = poll?.dates.map((d) => d.date) ?? [];
  const [d, setD] = useState<Draft>(() => ({
    dates: existingDates,
    message: poll?.message ?? '',
    participants: poll
      ? []
      : [
          ...(a.forester ? [{ role: 'forester' as const, contact_id: a.forester.id, name: a.forester.display_name, on: true }] : []),
          ...(a.partner ? [{ role: 'partner' as const, contact_id: a.partner.id, name: a.partner.display_name, on: true }] : []),
        ],
  }));
  const [noSundays, setNoSundays] = useState(true);
  // Kontakte aus der Suche oder externe Personen; derselbe Kontakt nur einmal
  const addParticipant = (x: { role: PollRole; contact_id: number | null; name: string }) =>
    setD((y) => (y.participants.some((z) => (x.contact_id ? z.contact_id === x.contact_id : !z.contact_id && z.name === x.name)) ? y : { ...y, participants: [...y.participants, { ...x, on: true }] }));
  const [err, setErr] = useState<string | null>(null);
  const today = todayIso();
  const selected = new Set(d.dates);
  const setDates = (list: string[]) => setD((x) => ({ ...x, dates: [...new Set(list)].sort() }));
  const toggle = (iso: string) => setDates(selected.has(iso) ? d.dates.filter((x) => x !== iso) : [...d.dates, iso]);
  // Ziehen: beginnt man auf einem gewählten Tag, wird der Zeitraum abgewählt, sonst hinzugefügt
  const range = (from: string, to: string, anchor: string) => {
    const days = daysBetween(from, to).filter((x) => x >= today && !(noSundays && isSunday(x)));
    setDates(selected.has(anchor) ? d.dates.filter((x) => !days.includes(x)) : [...d.dates, ...days]);
  };
  const save = useMutation({
    mutationFn: () => {
      const participants = d.participants.filter((x) => x.on).map(({ role, contact_id, name }) => ({ role, contact_id, name }));
      if (!poll) return api.post<PollDto>(`/actions/${a.id}/poll`, { dates: d.dates, participants, message: d.message || null } satisfies PollCreate);
      return api.patch<PollDto>(`/actions/${a.id}/poll`, {
        add_dates: d.dates.filter((x) => !existingDates.includes(x)),
        remove_dates: existingDates.filter((x) => !d.dates.includes(x)),
        add_participants: participants,
        message: d.message || null,
      });
    },
    onSuccess: () => (toast(poll ? 'Abstimmung geändert' : 'Abstimmung gestartet: Links jetzt verschicken'), onSaved()),
    onError: (e: Error) => setErr(e instanceof ApiRequestError && e.fields ? Object.values(e.fields).join(', ') : e.message),
  });
  const count = d.participants.filter((x) => x.on).length;
  return (
    <Modal
      title={poll ? 'Abstimmung bearbeiten' : 'Terminabstimmung starten'}
      subtitle={a.label}
      size="default"
      onClose={onClose}
      footer={
        <>
          {onDelete && (
            <button className="btn danger" type="button" style={{ marginRight: 'auto' }} onClick={onDelete}>
              Löschen
            </button>
          )}
          <button className="btn" type="button" onClick={onClose}>
            Abbrechen
          </button>
          <button className="btn primary" type="button" disabled={save.isPending || !d.dates.length || (!poll && !count)} onClick={() => save.mutate()}>
            {poll ? 'Speichern' : 'Starten'}
          </button>
        </>
      }
    >
      <div className="form one">
        <div className="field full">
          <label>
            Tage zur Abstimmung: {d.dates.length ? `${d.dates.length} ausgewählt` : 'Tage anklicken oder einen Zeitraum ziehen'}
          </label>
          <MonthCalendar
            start={(d.dates[0] ?? a.date ?? today).slice(0, 7)}
            min={today.slice(0, 7)}
            today={today}
            enabled={(x) => x >= today}
            dayClass={(x) => (selected.has(x) ? 'sel' : '')}
            onDayClick={toggle}
            onRange={range}
          />
          <div className="poll-cal-opts">
            <Checkbox value={noSundays} onChange={setNoSundays} label="Sonntage beim Ziehen auslassen" />
            {d.dates.length > 0 && (
              <button type="button" className="btn link-like small" onClick={() => setDates([])}>
                Auswahl leeren
              </button>
            )}
          </div>
        </div>
        {d.participants.length > 0 && (
          <div className="field full">
            <label>{poll ? 'Zusätzlich einladen' : 'Wer stimmt ab?'}</label>
            <div className="check-list">
              {d.participants.map((x, i) => (
                <Checkbox
                  key={i}
                  value={x.on}
                  onChange={(v) => setD((y) => ({ ...y, participants: y.participants.map((z, j) => (j === i ? { ...z, on: v } : z)) }))}
                  label={
                    <>
                      {x.name} <span className="mut small">({!x.contact_id ? 'extern' : x.role === 'other' ? 'Kontakt' : POLL_ROLE_LABELS[x.role]})</span>
                    </>
                  }
                />
              ))}
            </div>
          </div>
        )}
        <ContactPicker
          label="Weitere Person einladen"
          value={null}
          allowCreate={false}
          placeholder="Name eingeben, Kontakte werden durchsucht"
          onChange={(c) => c && addParticipant({ role: 'other', contact_id: c.id, name: c.display_name })}
          freeText={{ label: (q) => `„${q}“ als externe Person einladen`, onPick: (q) => addParticipant({ role: 'other', contact_id: null, name: q }) }}
        />
        <TextArea label="Hinweis auf der Abstimmungsseite" value={d.message} onChange={(v) => setD((y) => ({ ...y, message: v }))} rows={2} placeholder="z. B. Bitte bis Freitag eintragen. Wir sind mit zwei Teams unterwegs." />
        {err && <div className="note bad">{err}</div>}
      </div>
    </Modal>
  );
}
